import { useCallback, useEffect, useMemo, useState } from "react";
import { authApi } from "../api/endpoints";
import { changeLocale } from "../i18n";
import { SessionContext } from "./session-context";

/**
 * Sesión del portal. Se resuelve preguntando a la API: la cookie es httpOnly,
 * así que el cliente no puede leerla ni deducir nada de ella. Mientras se
 * resuelve, la aplicación muestra el estado de carga.
 */
export function SessionProvider({ children }) {
  const [state, setState] = useState({
    status: "loading",
    user: null,
    company: null,
    features: {},
  });
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;

    authApi.me().then(
      (data) => {
        if (cancelled) return;
        setState({
          status: "authenticated",
          user: data.user,
          company: data.company,
          features: data.features ?? {},
        });
        if (data.user?.locale) changeLocale(data.user.locale);
      },
      () => {
        if (!cancelled) {
          setState({
            status: "anonymous",
            user: null,
            company: null,
            features: {},
          });
        }
      },
    );

    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  const refresh = useCallback(() => setReloadToken((token) => token + 1), []);

  const finishSignIn = useCallback(async () => {
    const data = await authApi.me();
    setState({
      status: "authenticated",
      user: data.user,
      company: data.company,
      features: data.features ?? {},
    });
    if (data.user?.locale) changeLocale(data.user.locale);
  }, []);

  const signIn = useCallback(async (credentials) => {
    const result = await authApi.signIn(credentials);
    if (result.step !== "2FA") await finishSignIn();
    return result;
  }, [finishSignIn]);

  const verifySignIn = useCallback(async (payload) => {
    await authApi.verifySignIn(payload);
    await finishSignIn();
  }, [finishSignIn]);

  const signOut = useCallback(async () => {
    try {
      await authApi.signOut();
    } finally {
      setState({
        status: "anonymous",
        user: null,
        company: null,
        features: {},
      });
    }
  }, []);

  const updateUser = useCallback((user) => {
    setState((current) => ({ ...current, user }));
  }, []);

  const updateCompany = useCallback((company) => {
    setState((current) => ({ ...current, company }));
  }, []);

  const value = useMemo(
    () => ({ ...state, refresh, signIn, verifySignIn, signOut, updateUser, updateCompany }),
    [state, refresh, signIn, verifySignIn, signOut, updateUser, updateCompany],
  );

  return <SessionContext value={value}>{children}</SessionContext>;
}
